import { toast } from 'sonner';
import { isNativeApp } from './nativeBridge';

// One download path for every file the app produces (CSV, XLSX, PDF, ZIP…).
// Browser: <a download> click. Native wrapper: WKWebView renders "viewable"
// blobs (PDF, images…) inline instead of handing them to the wrapper's file
// handler, so those go through the bridge or the OS share sheet instead.

const MIME_BY_EXT = {
  pdf: 'application/pdf', csv: 'text/csv;charset=utf-8;', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel', zip: 'application/zip', json: 'application/json', txt: 'text/plain', png: 'image/png',
  jpg: 'image/jpeg', jpeg: 'image/jpeg', xml: 'application/xml', html: 'text/html',
};

const extOf = (name) => String(name || '').split('.').pop().toLowerCase();

export const mimeForFilename = (name) => MIME_BY_EXT[extOf(name)] || 'application/octet-stream';

// Types WKWebView can display itself - the wrapper's download handler never fires for these.
export const isViewableType = (mime, name) => {
  const m = String(mime || '').toLowerCase();
  if (m.startsWith('image/') || m.includes('pdf') || m.startsWith('text/html') || m.startsWith('text/plain') || m.includes('json')) return true;
  return ['pdf', 'png', 'jpg', 'jpeg', 'html', 'txt', 'json'].includes(extOf(name));
};

const blobToBase64 = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onloadend = () => resolve(String(r.result || '').split(',')[1] || '');
  r.onerror = reject;
  r.readAsDataURL(blob);
});

export const anchorDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 40000);
};

const shareFile = async (file) => {
  if (!navigator.share || !navigator.canShare || !navigator.canShare({ files: [file] })) return 'unsupported';
  try {
    await navigator.share({ files: [file], title: file.name });
    return 'shared';
  } catch (e) {
    if (e?.name === 'AbortError') return 'shared';
    if (e?.name === 'NotAllowedError') return 'gesture';
    return 'unsupported';
  }
};

// Native save for viewable blobs: bridge → share sheet → "tap to save" toast (share needs a fresh tap) → anchor.
const nativeSave = async (blob, filename) => {
  const bridge = window.NativeApp?.saveFile;
  if (typeof bridge === 'function') {
    try {
      await bridge({ name: filename, mime: blob.type, base64: await blobToBase64(blob) });
      return;
    } catch { /* fall through */ }
  }
  const file = new File([blob], filename, { type: blob.type });
  const result = await shareFile(file);
  if (result === 'shared') return;
  if (result === 'gesture') {
    toast.success(`${filename} is ready`, {
      id: `save-${filename}`,
      duration: 20000,
      description: 'Tap Save to choose where to keep it.',
      action: { label: 'Save', onClick: () => { shareFile(file).then((r) => { if (r !== 'shared') anchorDownload(blob, filename); }); } },
    });
    return;
  }
  anchorDownload(blob, filename);
};

export const saveBlob = (blobOrData, filename, mime) => {
  const type = mime || (blobOrData instanceof Blob && blobOrData.type) || mimeForFilename(filename);
  const blob = blobOrData instanceof Blob && blobOrData.type ? blobOrData : new Blob([blobOrData], { type });
  if (isNativeApp() && isViewableType(blob.type, filename)) return nativeSave(blob, filename);
  anchorDownload(blob, filename);
  return Promise.resolve();
};

// Catches every legacy `a.click()` download in the app when running inside the wrapper,
// so viewable blobs (PDFs) reach the user instead of being swallowed by WKWebView.
export const installNativeDownloadInterceptor = () => {
  if (typeof window === 'undefined' || window.__akfDownloadPatched) return;
  window.__akfDownloadPatched = true;
  // Remember blobs behind object URLs so a click can be resolved synchronously (callers often revoke right after click).
  const blobByUrl = new Map();
  const origCreate = URL.createObjectURL.bind(URL);
  URL.createObjectURL = (obj) => {
    const url = origCreate(obj);
    if (obj instanceof Blob) {
      blobByUrl.set(url, obj);
      if (blobByUrl.size > 30) blobByUrl.delete(blobByUrl.keys().next().value);
    }
    return url;
  };
  const nativeClick = HTMLAnchorElement.prototype.click;
  HTMLAnchorElement.prototype.click = function patchedClick() {
    const href = this.getAttribute('href') || '';
    const name = this.getAttribute('download');
    const blob = isNativeApp() && name != null && href.startsWith('blob:') ? blobByUrl.get(href) : null;
    if (blob) {
      const filename = name || 'download';
      const typed = blob.type ? blob : new Blob([blob], { type: mimeForFilename(filename) });
      if (isViewableType(typed.type, filename)) { nativeSave(typed, filename); return; }
    }
    return nativeClick.call(this);
  };
  // jsPDF's doc.save() uses window.saveAs when defined - route it through the same path.
  window.saveAs = (blob, name) => saveBlob(blob, name);
};
